import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import Sidebar from "../../componentes/Sidebar";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { fetchPublicCVEs, fetchCVESeverityCounts } from "../../api/cves";
import {
  faBug,
  faRadiation,
  faSkull,
  faTriangleExclamation,
  faSearch,
  faFilter,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FullscreenSpinner, InlineSpinner } from "../../componentes/LoadingIndicator";
import { API_BASE_URL } from "../../api/config";
import CVECard from "../../componentes/CVECard";
import Pagination from "../../componentes/Pagination";
import PropTypes from "prop-types";
// Precomputed CVSS values provided by public API; no vector parsing needed here

const baseUrl = API_BASE_URL;

// API fetch functions for news and alerts
const fetchNews = async (lang) => {
  // Add lang parameter
  const response = await fetch(`${baseUrl}/api/news?lang=${lang}`); // Include lang in query
  return response.json();
};

const fetchAlerts = async (lang) => {
  const response = await fetch(`${baseUrl}/api/alerts?lang=${lang}`);
  return response.json();
};

const SeverityFilter = ({
  selectedSeverity,
  onSeverityChange,
  count,
  severityCounts,
}) => {
  const formatCount = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
    return n.toString();
  };
  const severities = [
    {
      value: "all",
      label: "All",
      selectedColor: "bg-gray-200 text-gray-800",
      unselectedColor: "bg-gray-100 text-gray-600 hover:bg-gray-200",
      count: count,
    },
    {
      value: "critical",
      label: "Critical",
      selectedColor: "bg-red-200 text-red-800",
      unselectedColor: "bg-red-50 text-red-600 hover:bg-red-100",
      count: severityCounts?.critical || 0,
    },
    {
      value: "high",
      label: "High",
      selectedColor: "bg-orange-200 text-orange-800",
      unselectedColor: "bg-orange-50 text-orange-600 hover:bg-orange-100",
      count: severityCounts?.high || 0,
    },
    {
      value: "medium",
      label: "Medium",
      selectedColor: "bg-yellow-200 text-yellow-800",
      unselectedColor: "bg-yellow-50 text-yellow-600 hover:bg-yellow-100",
      count: severityCounts?.medium || 0,
    },
    {
      value: "low",
      label: "Low",
      selectedColor: "bg-green-200 text-green-800",
      unselectedColor: "bg-green-50 text-green-600 hover:bg-green-100",
      count: severityCounts?.low || 0,
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {severities.map((severity) => (
        <button
          key={severity.value}
          onClick={() => onSeverityChange(severity.value)}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${
            selectedSeverity === severity.value
              ? severity.selectedColor
              : severity.unselectedColor
          }`}
        >
          <span>{severity.label}</span>
          <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-white/50">
            {formatCount(severity.count)}
          </span>
        </button>
      ))}
      <span className="ml-3 text-xs text-gray-500 font-medium whitespace-nowrap">
        {count.toLocaleString()} CVE{count === 1 ? "" : "s"}
      </span>
    </div>
  );
};

SeverityFilter.propTypes = {
  selectedSeverity: PropTypes.string.isRequired,
  onSeverityChange: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  severityCounts: PropTypes.shape({
    critical: PropTypes.number,
    high: PropTypes.number,
    medium: PropTypes.number,
    low: PropTypes.number,
    none: PropTypes.number,
  }),
};

const CVE = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeverity, setSelectedSeverity] = useState("all");
  const itemsPerPage = 10;
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Reset to page 1 whenever search or severity changes — handlers used by inputs below
  const handleSeverityChange = useCallback((sev) => {
    setSelectedSeverity(sev);
    setCurrentPage(1);
  }, []);

  // Fetch CVE list using useQuery — severity and search are server-side
  const {
    data: cveQueryData,
    isLoading: cvesLoading,
  } = useQuery({
    queryKey: ["cves", currentPage, itemsPerPage, searchTerm, selectedSeverity],
    queryFn: () =>
      fetchPublicCVEs({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm.trim(),
        severity: selectedSeverity,
      }),
    keepPreviousData: true,
  });

  // Fetch global severity counts once — independent of pagination/search/filter
  const { data: severityCountsData } = useQuery({
    queryKey: ["cve-severity-counts"],
    queryFn: fetchCVESeverityCounts,
    staleTime: 5 * 60 * 1000, // reuse for 5 minutes — counts don't change often
  });

  // Fetch news and alerts for sidebar using useQuery
  const { data: newsData, isLoading: newsLoading } = useQuery({
    queryKey: ["news", lang], // Add lang to queryKey
    queryFn: () => fetchNews(lang), // Pass lang to fetchNews
  });

  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ["alerts", lang], // Add lang to queryKey
    queryFn: () => fetchAlerts(lang), // Pass lang to fetchEvents
  });

  const news = newsData?.results || newsData || [];
  const alerts = alertsData?.results || alertsData || [];
  const loadingSidebar = newsLoading || alertsLoading;

  // Extract and normalize data for rendering
  // Helper to compute recency timestamp (newest first)
  const getRecencyTs = useCallback((cve) => {
    const dt = cve?.updated_at || cve?.last_modified || cve?.published_at || null;
    const t = dt ? Date.parse(dt) : NaN;
    return Number.isNaN(t) ? 0 : t;
  }, []);

  const rawCves = useMemo(() => {
    const list = cveQueryData?.cves || [];
    // Ensure newest first within the page
    return [...list].sort((a, b) => getRecencyTs(b) - getRecencyTs(a));
  }, [cveQueryData, getRecencyTs]);

  // Pick best-available CVSS: 4.0 -> 3.1 -> 3.0 -> 2.0
  // New API: vectors are in cvss40/cvss31/cvss30/cvss2 fields; scores in corresponding *_score; severities in *_severity
  const pickCVSS = (cve) => {
    const has = (v) => v !== undefined && v !== null && String(v).trim() !== "";
    const num = (v) => (has(v) ? Number(v) : null);

    // Derive severity from score when the label is missing (e.g. old CVSS v2 entries)
    const deriveSeverity = (label, score) => {
      if (label) return label;
      if (typeof score === "number") {
        if (score >= 9.0) return "CRITICAL";
        if (score >= 7.0) return "HIGH";
        if (score >= 4.0) return "MEDIUM";
        if (score > 0)    return "LOW";
      }
      return null;
    };

    if (has(cve.cvss40) || has(cve.cvss40_score) || has(cve.cvss40_severity)) {
      const score = num(cve.cvss40_score);
      return { version: "4.0", score, severity: deriveSeverity(cve.cvss40_severity, score), vector: has(cve.cvss40) ? cve.cvss40 : null };
    }
    if (has(cve.cvss31) || has(cve.cvss31_score) || has(cve.cvss31_severity)) {
      const score = num(cve.cvss31_score);
      return { version: "3.1", score, severity: deriveSeverity(cve.cvss31_severity, score), vector: has(cve.cvss31) ? cve.cvss31 : null };
    }
    if (has(cve.cvss30) || has(cve.cvss30_score) || has(cve.cvss30_severity)) {
      const score = num(cve.cvss30_score);
      return { version: "3.0", score, severity: deriveSeverity(cve.cvss30_severity, score), vector: has(cve.cvss30) ? cve.cvss30 : null };
    }
    if (has(cve.cvss2) || has(cve.cvss2_score) || has(cve.cvss2_severity)) {
      const score = num(cve.cvss2_score);
      return { version: "2.0", score, severity: deriveSeverity(cve.cvss2_severity, score), vector: has(cve.cvss2) ? cve.cvss2 : null };
    }
    return { version: null, score: null, severity: null, vector: null };
  };

  const cves = useMemo(
    () =>
      rawCves.map((c) => ({
        ...c,
        __cvss: pickCVSS(c),
        __code: c.cve_code || c.id,
      })),
    [rawCves]
  );

  const totalPages = cveQueryData?.total_pages || 1;
  const currentApiPage = cveQueryData?.current_page || currentPage;
  const hasNextPage = cveQueryData?.has_next ?? currentApiPage < totalPages;
  const hasPreviousPage = cveQueryData?.has_previous ?? currentApiPage > 1;
  const totalResults = severityCountsData?.total ?? 0;

  // Client-side text search is kept only as a within-page highlight aid.
  // Severity filtering is fully server-side — no client filtering on severity here.
  const filteredCVEs = useMemo(() => {
    if (!searchTerm.trim()) return cves;
    const term = searchTerm.trim().toLowerCase();
    return cves.filter(
      (cve) =>
        (cve.__code && cve.__code.toLowerCase().includes(term)) ||
        (cve.description && cve.description.toLowerCase().includes(term))
    );
  }, [cves, searchTerm]);

  // Severity counts come from the dedicated endpoint — real totals, not current-page counts
  const severityCounts = useMemo(() => ({
    critical: severityCountsData?.critical?.count ?? 0,
    high:     severityCountsData?.high?.count     ?? 0,
    medium:   severityCountsData?.medium?.count   ?? 0,
    low:      severityCountsData?.low?.count      ?? 0,
    none:     severityCountsData?.unscored?.count ?? 0,
  }), [severityCountsData]);

  // Calculate per-page statistics (exploit active, recent) — still based on current page
  const statistics = useMemo(() => {
    const exploitActive = cves.filter((cve) => cve.exploit_active).length;
    const recentCVEs = cves.filter((cve) => {
      const publishedDate = new Date(cve.published_at);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return publishedDate >= thirtyDaysAgo;
    }).length;

    return {
      total: severityCountsData?.total ?? cves.length,
      exploitActive,
      recent: recentCVEs,
      severityCounts,
    };
  }, [cves, severityCounts, severityCountsData]);

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage && currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  }, []);
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setCurrentPage(1);
  }, []);

  // Add a helper to highlight search term
  const highlightText = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(
      `(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    return text.split(regex).map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Render logic functions
  const renderPageContent = () => {
    if (cvesLoading) {
      return (
        <FullscreenSpinner message="Loading CVEs..." tone="slate" />
      );
    }

    if (!filteredCVEs || filteredCVEs.length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">
          <div className="max-w-md mx-auto">
            <p className="text-lg font-medium mb-2">
              No results found{searchTerm ? ` for "${searchTerm}"` : ""}
            </p>
            <p className="text-sm mb-4">
              {searchTerm
                ? "Try adjusting your search terms or browse all CVEs."
                : "Try searching for specific CVE IDs or keywords."}
            </p>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Clear Search & Show All CVEs
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* CVE Cards */}
        <div className="space-y-4">
          {filteredCVEs.map((cve) => (
            <CVECard
              key={cve.id}
              cve={{
                ...cve,
                highlightedId: highlightText(cve.__code, searchTerm),
                description: highlightText(cve.description, searchTerm),
                cvss: cve.__cvss,
                code: cve.__code,
              }}
            />
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <>
            <div className="text-center text-sm text-gray-600 mt-4">
              Page <span className="font-semibold">{currentApiPage}</span> of{" "}
              <span className="font-semibold">{totalPages}</span>
              {totalResults > 0 && (
                <span className="ml-2 text-gray-500">
                  ({filteredCVEs.length} of {totalResults} results)
                </span>
              )}
            </div>
            <Pagination
              currentPage={currentApiPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              previousLabel="Previous"
              nextLabel="Next"
            />
          </>
        )}
      </div>
    );
  };

  return (
    // Added overflow-x-hidden and max width container utilities to prevent horizontal scroll
    <div className="overflow-x-hidden">
      <PageHeader />
      <GradientBanner
        title={t("cves.title")}
        description={t("cves.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("cves.title"), link: null },
        ]}
        backgroundIcons={[faBug, faRadiation, faSkull, faTriangleExclamation]}
      />
      <div className="min-h-screen">
        {/* Constrain overall width and ensure no overflow */}
        <div className="flex flex-col max-xl:px-4 py-4 md:py-8 px-dynamic w-full max-w-[1440px] mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-2 xl:gap-6 mt-6 w-full">
            <div className="md:col-span-2 w-full overflow-x-hidden">
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Search Bar */}
                  <div className="space-y-3">
                    <label
                      htmlFor="cve-search"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Search CVEs
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon
                          icon={faSearch}
                          className="h-4 w-4 text-gray-400"
                        />
                      </div>
                      <input
                        type="text"
                        id="cve-search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search by CVE code"
                        className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                      {searchTerm && (
                        <button
                          onClick={handleClearSearch}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          title="Clear search"
                        >
                          <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {/* Search status */}

                    {searchTerm && (
                      <div className="text-sm text-gray-600">
                        {cvesLoading ? (
                          <div className="flex items-center">
                            <InlineSpinner message={`Searching for "${searchTerm}"...`} tone="slate" />
                          </div>
                        ) : (
                          <div>
                            {filteredCVEs.length > 0 ? (
                              <span>
                                {cveQueryData.total_pages > 1 ? (
                                  <>
                                    Found approximately{" "}
                                    {totalResults > 0
                                      ? totalResults.toLocaleString()
                                      : cveQueryData.total_pages * itemsPerPage}{" "}
                                    results for &quot;{searchTerm}&quot;
                                    <span className="text-gray-500 ml-1">
                                      (showing {filteredCVEs.length} on page{" "}
                                      {currentApiPage} of{" "}
                                      {cveQueryData.total_pages})
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    Found {filteredCVEs.length} result
                                    {filteredCVEs.length === 1 ? "" : "s"} for
                                    &quot;{searchTerm}&quot;
                                  </>
                                )}
                              </span>
                            ) : (
                              <span className="text-red-600">
                                No results found for &quot;{searchTerm}&quot;
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Severity Filter */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <FontAwesomeIcon icon={faFilter} className="h-4 w-4" />
                      Filter by Severity
                    </label>
                    <div>
                      <SeverityFilter
                        selectedSeverity={selectedSeverity}
                        onSeverityChange={handleSeverityChange}
                        count={totalResults}
                        severityCounts={statistics.severityCounts}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Ensure no horizontal scroll; allow vertical overflow for tooltips */}
              <div className="w-full break-words overflow-x-hidden overflow-y-visible">
                {renderPageContent()}
              </div>
            </div>

            <Sidebar
              primaryItems={news}
              CNS
              secondaryItems={alerts}
              loading={loadingSidebar}
              primaryTitle="news"
              secondaryTitle="alerts"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVE;
